import {makeAutoObservable, runInAction} from 'mobx';

import type {UserDetailsWithMFA} from '@/app/[locale]/user/settings/account-security/AccountSecurityStore';
import type {ApiResponse} from '@/models/ApiResponse';
import type {IActiveOrgConnection} from '@/models/Connections';
import type {IMFAAuth} from '@/models/MFAAuth';
import type {hasPasswordExpiredResponse, LandingPreference, User} from '@/models/User';
import {USER_URLS} from '@/utils/constants/api-urls';
import {getNotificationFromError} from '@/utils/helpers';
import axios from '@/utils/helpers/axios';

import type NotificationStore from './NotificationStore';

export default class UserStore {
  public user: User | null = null;
  public passwordExpirationDetails: hasPasswordExpiredResponse | null = null;
  public isFetchingUser = false;
  public notificationStore: NotificationStore;
  public orgs: IActiveOrgConnection[] = [];
  public isFetchingOrgs = false;
  constructor(notificationStore: NotificationStore) {
    makeAutoObservable(this);
    this.notificationStore = notificationStore;
  }

  public fetchOrgConnections = async () => {
    this.isFetchingOrgs = true;
    try {
      const {
        data: {data, succeeded},
      } = await axios.get<ApiResponse<IActiveOrgConnection[]>>(USER_URLS.ORG_CONNECTIONS);

      runInAction(() => {
        if (succeeded) this.orgs = data;
      });
    } catch (err: any) {
      const notification = getNotificationFromError(err);
      this.notificationStore.addNotification(notification);
    } finally {
      this.isFetchingOrgs = false;
    }
  };

  public updatePasswordExpirationDetails = () => {
    if (this.passwordExpirationDetails) {
      this.passwordExpirationDetails = {...this.passwordExpirationDetails, isPasswordExpired: false};
    }
  };

  public fetchUser = async () => {
    this.isFetchingUser = true;
    try {
      const {
        data: {data, succeeded},
      } = await axios.get<ApiResponse<User>>(USER_URLS.GET_LOGGED_IN_USER);
      runInAction(() => {
        if (succeeded) this.user = data;
        this.fetchOrgConnections();
      });
    } catch (err: any) {
      const notification = getNotificationFromError(err);
      this.notificationStore.addNotification(notification);
    } finally {
      this.isFetchingUser = false;
    }
  };

  public hasPasswordExpired = async () => {
    try {
      const axiosResponse = await axios.get(USER_URLS.HAS_PASSWORD_EXPIRED);
      this.passwordExpirationDetails = axiosResponse.data.data;
    } catch (err: any) {
      const notification = getNotificationFromError(err);
      this.notificationStore.addNotification(notification);
    }
  };

  public async updateUserLandingPagePreference(preference: LandingPreference) {
    try {
      const response = await axios.post<ApiResponse<User>>(USER_URLS.UPDATE_LANDING_PAGE_PREFERENCE, preference);
      return response.data;
    } catch (err) {}
  }

  public updateLocale = async (paramName: string, paramValue: string, userId?: string, location?: string) => {
    if (this.user) {
      const {
        data: {data, succeeded},
      } = await axios.patch<ApiResponse<User>>(USER_URLS.UPDATE_LOCALE(userId || this.user.guid, location || this.user.location), {
        updatedParam: paramName,
        updatedValue: paramValue,
      });
      if (!userId || userId === this.user.guid) {
        runInAction(() => {
          if (succeeded && paramName !== 'language') {
            this.user = data;
          }
        });
      }
      return data;
    }
  };
  public setUserDetails = (user: User | null) => {
    runInAction(() => {
      this.user = user;
    });
  };

  public clearUser = () => {
    this.setUserDetails(null);
  };

  updatePrimaryEmail = async (auth: IMFAAuth) => {
    try {
      const response = await axios.put<ApiResponse<UserDetailsWithMFA>>(USER_URLS.UPDATE_PRIMARY_EMAIL, auth);
      if (response.data.succeeded && response.data.data.isVerified) {
        runInAction(() => (this.user = response.data.data.userDetails));
      }

      return response.data;
    } catch (err) {}
  };
}
